import Markdown from "react-markdown";
import { AuthImage } from "@/components/AuthImage";
import { RecommendationView } from "@/components/RecommendationView";
import { QuickChoices } from "@/components/QuickChoices";

const FENCE = "```recommendation";
const FOLLOW_UPS = {
  questions: [{
    id: "follow-up", label: "Want to go deeper?", multi: false,
    options: ["Explain this more simply", "Give me more detail", "Why is Option 1 the best fit?", "Compare the options", "What should I check on the bottle?", "What could change the healed color?"],
  }],
};
const CHOICE_FENCE = "```choices";

function extractChoices(content) {
  const start = content.indexOf(CHOICE_FENCE);
  if (start === -1) return { text: content, choices: null, choicesPending: false };
  const rest = content.slice(start + CHOICE_FENCE.length);
  const end = rest.indexOf("```");
  if (end === -1) {
    try {
      return { text: content.slice(0, start), choices: JSON.parse(rest.trim()), choicesPending: false };
    } catch (e) {
      return { text: content.slice(0, start), choices: null, choicesPending: true };
    }
  }
  let choices = null;
  try {
    choices = JSON.parse(rest.slice(0, end));
  } catch (e) {
    choices = null;
  }
  return { text: content.slice(0, start) + rest.slice(end + 3), choices, choicesPending: false };
}

function splitContent(content) {
  const start = content.indexOf(FENCE);
  if (start === -1) return { before: content, pending: false, after: "" };
  const rest = content.slice(start + FENCE.length);
  const end = rest.indexOf("```");
  return { before: content.slice(0, start), pending: end === -1, after: end === -1 ? "" : rest.slice(end + 3) };
}

function Composing() {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-[#E6DBCF] bg-[#FDF2F0] px-5 py-4 text-sm text-[#6E5549]" data-testid="composing-recommendation">
      <span className="flex gap-1">
        {[0, 1, 2].map((i) => <span key={i} className="ig-dot h-1.5 w-1.5 rounded-full bg-[#C5A059]" style={{ animationDelay: `${i * 0.2}s` }} />)}
      </span>
      Composing your structured pigment pathways…
    </div>
  );
}

export function MessageBubble({ m, streaming, onChoose }) {
  if (m.role === "user") {
    return (
      <div className="ig-rise flex justify-end" data-testid={`message-user-${m.id}`}>
        <div className={`${m.recommendation ? "w-full min-w-0" : "max-w-[85%]"} space-y-2`}>
          {m.image_ids?.length > 0 && (
            <div className="flex flex-wrap justify-end gap-2">
              {m.image_ids.map((id) => <AuthImage key={id} id={id} testId={`message-image-${id}`} className="h-28 w-28 rounded-xl border border-[#E6DBCF] object-cover" />)}
            </div>
          )}
          {m.content && <div className="whitespace-pre-wrap rounded-2xl rounded-tr-sm bg-[#3D2317] px-4 py-3 text-sm leading-relaxed text-[#FDFBF7]">{m.content}</div>}
        </div>
      </div>
    );
  }
  const { text, choices } = extractChoices(m.content || "");
  const { before, pending, after } = splitContent(text);
  return (
    <div className="ig-rise flex gap-3" data-testid={`message-assistant-${m.id}`}>
      <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#2C1810] font-serif text-sm italic text-[#C5A059]">IG</div>
      <div className="min-w-0 flex-1 space-y-4">
        {before.trim() && (
          <div className="ig-prose rounded-2xl rounded-tl-sm border border-[#E6DBCF] bg-[#FDFBF7] px-5 py-4 text-sm text-[#4A3B32]">
            <Markdown>{before}</Markdown>
          </div>
        )}
        {pending && streaming && <Composing />}
        {m.recommendation && <RecommendationView rec={m.recommendation} mid={m.id} />}
        {!pending && !m.recommendation && m.content?.includes(FENCE) && !streaming && (
          <p className="text-xs text-[#8C2A2A]">The structured recommendation could not be displayed. Ask Ink Genius to restate it.</p>
        )}
        {after.trim() && (
          <div className="ig-prose px-1 text-sm text-[#4A3B32]"><Markdown>{after}</Markdown></div>
        )}
        {onChoose && !streaming && (choices || m.recommendation) && (
          <QuickChoices key={m.id} choices={choices || FOLLOW_UPS} onSubmit={onChoose} mid={m.id} />
        )}
      </div>
    </div>
  );
}
